/* De doorverwijzingen van de site (/crm/website/site/doorverwijzingen, ADR 0024): een oud adres dat
 * naar een nieuw adres wijst. Dit bestand tekent alleen. Wat een geldige doorverwijzing is, wat er
 * geweigerd wordt en wat er wordt geschreven, staat in data/doorverwijzingen.ts.
 *
 * Eén scherm: bovenaan een nieuwe toevoegen, daaronder de lijst. Van een bestaande doorverwijzing zijn
 * het doel, de status en de notitie te wijzigen. Het oude adres is de sleutel en ligt vast. Een foute
 * doorverwijzing is te verwijderen, na een bevestiging die het oude adres en het doel noemt: het enige
 * in Websitecontent dat verwijderd kan worden (besluit Stan, 10-10-2026). Na het opslaan of verwijderen
 * vraagt de CRM de site om te verversen (ververs.ts, ADR 0031). */

import { useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, Pencil, Plus, Save, Trash2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useDoorverwijzingMaken, useDoorverwijzingVerwijderen, useDoorverwijzingWijzigen, useDoorverwijzingen, useRechten } from './hooks';
import {
  LEEG, STATUSSEN, controleerNieuw, controleerWijziging, opmerkingen, statusNaam, wijziging,
  type Concept, type Doorverwijzing, type Keuring, type Pagina, type Wijziging,
} from './data/doorverwijzingen';
import { LATER_DOORVERWIJZING, LATER_VERWIJDERD, NU_DOORVERWIJZING, NU_VERWIJDERD, meldOpslag } from './ververs';
import { Fout, Kop } from './WebsiteBeheer';

const TERUG = { naar: '/crm/website/site', label: 'Content op de site' };
const TITEL = 'Doorverwijzingen';
const KEUZE = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50';
const STATUS_UITLEG = 'Voor de site is 301 hetzelfde als 308 (blijvend) en 302 hetzelfde als 307 (tijdelijk). Kies blijvend, tenzij het oude adres terugkomt.';

export function WebsiteDoorverwijzingen() {
  const { magBewerken } = useRechten();
  const { data, error, isLoading } = useDoorverwijzingen();

  if (isLoading) return <p className="text-sm text-muted-foreground">Laden…</p>;
  if (error || !data) {
    return (
      <div className="space-y-4">
        <Kop titel={TITEL} terug={TERUG} />
        <Fout tekst={error ? (error as Error).message : 'De doorverwijzingen zijn niet te lezen.'} />
      </div>
    );
  }
  const { doorverwijzingen, paginas } = data;
  return (
    <div className="space-y-4">
      <Kop titel={TITEL} terug={TERUG} />
      <p className="max-w-3xl text-sm text-muted-foreground" data-uitleg="doorverwijzingen">
        De site kijkt alleen in deze lijst als er op een adres geen pagina staat: staat het adres hier, dan stuurt ze de bezoeker door naar het doel, en anders
        krijgt hij de pagina &quot;niet gevonden&quot;. Verander je in de CRM de slug van een pagina, dan komt de doorverwijzing er vanzelf bij.
        Een doorverwijzing die niet klopt, kun je verwijderen.
      </p>
      {!magBewerken && <Fout tekst="Je kunt de doorverwijzingen lezen, maar niet wijzigen." />}
      {magBewerken && <Nieuw bestaand={doorverwijzingen} paginas={paginas} />}
      <Lijst bestaand={doorverwijzingen} paginas={paginas} magBewerken={magBewerken} />
    </div>
  );
}

/* ── Wat de keuring zegt, onder de vakken ──────────────────────────────────── */

/** Bij een keten: één klik zet het einddoel in het vak Doel. Staat direct onder de melding. */
function Voorstel({ keuring, kies, uit }: { keuring: Keuring; kies: (doel: string) => void; uit: boolean }) {
  if (!keuring.voorstel) return null;
  return (
    <Button variant="outline" size="sm" className="h-auto whitespace-normal break-all py-1.5 text-left" disabled={uit} onClick={() => kies(keuring.voorstel!)} data-voorstel={keuring.voorstel}>
      Wijs direct naar {keuring.voorstel}
    </Button>
  );
}

/** Wat het opslaan niet tegenhoudt, maar goed is om te weten. */
function LetOp({ keuring }: { keuring: Keuring }) {
  return (
    <>
      {keuring.letOp.map((zin) => (
        <p key={zin} className="flex items-start gap-1.5 text-xs text-amber-600" data-let-op="doorverwijzing">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{zin}</span>
        </p>
      ))}
    </>
  );
}

function StatusKeuze({ id, waarde, uit, zet }: { id: string; waarde: number | string; uit: boolean; zet: (status: number) => void }) {
  return (
    <select id={id} className={KEUZE} value={String(waarde)} disabled={uit} onChange={(e) => zet(Number(e.target.value))}>
      {STATUSSEN.map((status) => <option key={status} value={status}>{statusNaam(status)}</option>)}
    </select>
  );
}

/* ── Nieuw ─────────────────────────────────────────────────────────────────── */

function Nieuw({ bestaand, paginas }: { bestaand: Doorverwijzing[]; paginas: Pagina[] }) {
  const { toast } = useToast();
  const [concept, setConcept] = useState<Concept>(LEEG);
  const maken = useDoorverwijzingMaken();
  const uit = maken.isPending;
  const keuring = useMemo(() => controleerNieuw(concept, bestaand, paginas), [concept, bestaand, paginas]);
  const zet = (vak: keyof Concept, waarde: string | number) => setConcept((c) => ({ ...c, [vak]: waarde }));
  // Een leeg vak is nog niet fout: de melding komt pas als er iets staat. De knop blijft wel uit.
  const fout = (vak: 'from_path' | 'to_path') => (concept[vak].trim() === '' ? undefined : keuring.fouten[vak]);

  const bewaar = async () => {
    try {
      const uitkomst = await maken.mutateAsync(concept);
      setConcept(LEEG);
      // De melding staat er meteen; of de site al ververst is, komt er even later bij.
      void meldOpslag(toast, uitkomst.verversing, 'Doorverwijzing toegevoegd', '', LATER_DOORVERWIJZING, NU_DOORVERWIJZING);
    } catch (mis) {
      toast({ title: 'Niet toegevoegd', description: mis instanceof Error ? mis.message : String(mis), variant: 'destructive' });
    }
  };

  return (
    <Card data-kaart="nieuw">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Nieuwe doorverwijzing</CardTitle>
        <p className="text-sm text-muted-foreground">
          Een doorverwijzing werkt meteen na het opslaan: er is geen concept. Het oude adres kun je daarna niet meer wijzigen.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="nieuw-from_path">Oud adres *</Label>
            <Input id="nieuw-from_path" value={concept.from_path} placeholder="/oud-adres" disabled={uit} onChange={(e) => zet('from_path', e.target.value)} />
            <p className="text-xs text-muted-foreground">Het pad na greenvision.energy, in kleine letters en zonder / aan het eind.</p>
            {fout('from_path') && <p className="text-xs text-destructive" role="alert">{fout('from_path')}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nieuw-to_path">Doel *</Label>
            <Input id="nieuw-to_path" value={concept.to_path} placeholder="/nieuw-adres" disabled={uit} onChange={(e) => zet('to_path', e.target.value)} />
            <p className="text-xs text-muted-foreground">Een pad op de site dat met / begint, of een heel adres dat met https:// begint.</p>
            {fout('to_path') && <p className="text-xs text-destructive" role="alert">{fout('to_path')}</p>}
            <Voorstel keuring={keuring} uit={uit} kies={(doel) => zet('to_path', doel)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nieuw-status">Status</Label>
            <StatusKeuze id="nieuw-status" waarde={concept.status} uit={uit} zet={(status) => zet('status', status)} />
            <p className="text-xs text-muted-foreground">{STATUS_UITLEG}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nieuw-note">Notitie</Label>
            <Input id="nieuw-note" value={concept.note} placeholder="Waarom deze doorverwijzing er is" disabled={uit} onChange={(e) => zet('note', e.target.value)} />
            <p className="text-xs text-muted-foreground">Alleen voor de CRM: de site toont de notitie niet.</p>
          </div>
        </div>
        <LetOp keuring={keuring} />
        <div className="flex justify-end">
          <Button onClick={bewaar} disabled={uit || !keuring.ok} size="sm">
            <Plus className="mr-1 h-4 w-4" /> {uit ? 'Toevoegen…' : 'Toevoegen'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── De lijst, met per rij het bewerken van doel, status en notitie ────────── */

function Lijst({ bestaand, paginas, magBewerken }: { bestaand: Doorverwijzing[]; paginas: Pagina[]; magBewerken: boolean }) {
  // Eén rij tegelijk open: het oude adres is de sleutel.
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card data-kaart="lijst">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{bestaand.length === 1 ? '1 doorverwijzing' : `${bestaand.length} doorverwijzingen`}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {bestaand.length === 0 && <p className="px-6 pb-6 text-sm text-muted-foreground">Nog geen doorverwijzingen.</p>}
        <ul className="divide-y">
          {bestaand.map((rij) => (
            <Regel key={rij.from_path} rij={rij} bestaand={bestaand} paginas={paginas} magBewerken={magBewerken}
              open={open === rij.from_path} openen={() => setOpen(rij.from_path)} sluiten={() => setOpen(null)} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function Regel({ rij, bestaand, paginas, magBewerken, open, openen, sluiten }: {
  rij: Doorverwijzing; bestaand: Doorverwijzing[]; paginas: Pagina[]; magBewerken: boolean; open: boolean; openen: () => void; sluiten: () => void;
}) {
  const bij = useMemo(() => opmerkingen(rij, bestaand, paginas), [rij, bestaand, paginas]);
  return (
    <li className="space-y-3 px-6 py-3" data-van={rij.from_path} data-werkt={bij.length === 0}>
      {/* Op een smal scherm staat de knop onder de adressen, op een breed scherm er altijd rechts naast. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <code className="break-all font-medium">{rij.from_path}</code>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="gaat naar" />
            <code className="break-all">{rij.to_path}</code>
            <Badge variant="secondary">{statusNaam(rij.status)}</Badge>
          </div>
          {rij.note && <p className="text-xs text-muted-foreground">{rij.note}</p>}
          {bij.map((zin) => (
            <p key={zin} className="flex items-start gap-1.5 text-xs text-amber-600" data-opmerking>
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{zin}</span>
            </p>
          ))}
        </div>
        {magBewerken && !open && (
          <div className="flex shrink-0 flex-wrap gap-1 self-start">
            <Button variant="ghost" size="sm" onClick={openen}><Pencil className="mr-1 h-4 w-4" /> Bewerken</Button>
            <Verwijder rij={rij} />
          </div>
        )}
      </div>
      {open && <Bewerk rij={rij} bestaand={bestaand} paginas={paginas} sluiten={sluiten} />}
    </li>
  );
}

/** De knop Verwijderen bij een rij, met de bevestiging. Die noemt het oude adres en het doel, en zegt
 *  wat de site daarna doet. Verwijderen kan in Websitecontent alleen hier. */
function Verwijder({ rij }: { rij: Doorverwijzing }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const verwijderen = useDoorverwijzingVerwijderen();
  const bezig = verwijderen.isPending;

  const verwijder = async () => {
    try {
      const uitkomst = await verwijderen.mutateAsync(rij.from_path);
      // De melding staat er meteen; of de site al ververst is, komt er even later bij.
      void meldOpslag(toast, uitkomst.verversing, 'Doorverwijzing verwijderd', '', LATER_VERWIJDERD, NU_VERWIJDERD);
    } catch (mis) {
      toast({ title: 'Niet verwijderd', description: mis instanceof Error ? mis.message : String(mis), variant: 'destructive' });
    } finally {
      setOpen(false);
    }
  };

  return (
    <>
      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setOpen(true)} data-verwijder={rij.from_path}>
        <Trash2 className="mr-1 h-4 w-4" /> Verwijderen
      </Button>
      {/* Zolang het verwijderen loopt, blijft het venster staan: sluiten kan dan niet. */}
      <AlertDialog open={open} onOpenChange={(aan) => { if (!bezig) setOpen(aan); }}>
        <AlertDialogContent className="w-[calc(100vw-1.5rem)] rounded-lg" data-bevestig-verwijderen={rij.from_path}>
          <AlertDialogHeader>
            <AlertDialogTitle>Deze doorverwijzing verwijderen?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left">
                <p>
                  Het oude adres <code className="break-all font-medium text-foreground">{rij.from_path}</code> verwijst nu door naar{' '}
                  <code className="break-all font-medium text-foreground">{rij.to_path}</code>.
                </p>
                <p>
                  Na het verwijderen doet de site dat niet meer. Wie het oude adres opent, krijgt dan de pagina &quot;niet gevonden&quot;, tenzij er op dat adres een
                  pagina staat.
                </p>
                <p>Terughalen kan niet. Wil je hem terug, dan voeg je hem opnieuw toe.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bezig}>Annuleren</AlertDialogCancel>
            {/* preventDefault: het venster sluit pas als bekend is of het verwijderen is gelukt. */}
            <AlertDialogAction className={buttonVariants({ variant: 'destructive' })} disabled={bezig} onClick={(e) => { e.preventDefault(); void verwijder(); }}>
              {bezig ? 'Verwijderen…' : 'Verwijder de doorverwijzing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Bewerk({ rij, bestaand, paginas, sluiten }: { rij: Doorverwijzing; bestaand: Doorverwijzing[]; paginas: Pagina[]; sluiten: () => void }) {
  const { toast } = useToast();
  const [concept, setConcept] = useState<Wijziging>({ to_path: rij.to_path, status: rij.status, note: rij.note ?? '' });
  const wijzigen = useDoorverwijzingWijzigen();
  const uit = wijzigen.isPending;
  const keuring = useMemo(() => controleerWijziging(rij, concept, bestaand, paginas), [rij, concept, bestaand, paginas]);
  const gewijzigd = Object.keys(wijziging(rij, concept)).length > 0;
  const zet = (vak: keyof Wijziging, waarde: string | number) => setConcept((c) => ({ ...c, [vak]: waarde }));
  const id = (vak: string) => `bewerk-${vak}`;

  const bewaar = async () => {
    try {
      const uitkomst = await wijzigen.mutateAsync({ vorige: rij, concept });
      sluiten();
      if (uitkomst.geschreven.length === 0) toast({ title: 'Niets gewijzigd', description: 'Er is niets opgeslagen.' });
      // De site leest de notitie niet: dan is er ook niets te verversen.
      else if (!uitkomst.verversing) toast({ title: 'Opgeslagen', description: 'De notitie staat alleen in de CRM: op de site verandert niets.' });
      else void meldOpslag(toast, uitkomst.verversing, 'Opgeslagen', '', LATER_DOORVERWIJZING, NU_DOORVERWIJZING);
    } catch (mis) {
      toast({ title: 'Niet opgeslagen', description: mis instanceof Error ? mis.message : String(mis), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4 rounded-md border bg-muted/30 p-3" data-bewerk={rij.from_path}>
      <p className="text-xs text-muted-foreground">
        Het oude adres <code>{rij.from_path}</code> ligt vast. Klopt het niet, verwijder deze doorverwijzing dan en voeg een nieuwe toe.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor={id('to_path')}>Doel *</Label>
          <Input id={id('to_path')} value={concept.to_path} disabled={uit} onChange={(e) => zet('to_path', e.target.value)} />
          {keuring.fouten.to_path && <p className="text-xs text-destructive" role="alert">{keuring.fouten.to_path}</p>}
          <Voorstel keuring={keuring} uit={uit} kies={(doel) => zet('to_path', doel)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={id('status')}>Status</Label>
          <StatusKeuze id={id('status')} waarde={concept.status} uit={uit} zet={(status) => zet('status', status)} />
          <p className="text-xs text-muted-foreground">{STATUS_UITLEG}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={id('note')}>Notitie</Label>
          <Input id={id('note')} value={concept.note} disabled={uit} onChange={(e) => zet('note', e.target.value)} />
        </div>
      </div>
      <LetOp keuring={keuring} />
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" size="sm" disabled={uit} onClick={sluiten}>Annuleren</Button>
        <Button onClick={bewaar} disabled={uit || !gewijzigd || !keuring.ok} size="sm">
          <Save className="mr-1 h-4 w-4" /> {uit ? 'Opslaan…' : 'Opslaan'}
        </Button>
      </div>
    </div>
  );
}
